import {Section} from "../../Section";
import {accountStore} from "../../../../../Stores/AccountStore";
import {Box, FormControl, InputLabel, MenuItem, Radio, Select, Typography, useTheme} from "@mui/material";
import React, {Fragment, useEffect, useRef, useState} from "react";
import Column from "../../../../Common/Column";
import Row, {CenterRow} from "../../../../Common/Row";
import Space from "../../../../Common/Space";
import {getUnixTime} from "../../../../../Utilities/dateUtils";
import PictureRender from "../../../../Shared/Pictures/PictureRender";
import {ErrorText} from "./ErrorText";
import {TierCard} from "./TierCard";
import {LastTierCard} from "./LastTierCard";
import {PaymentItem} from "./PaymentItem";
import {useMediaContext} from "../../../../../MediaContext";
import {TierRow} from "./TierRow";
import {oreToKroner} from "../../../../../Utilities/postUtils";
import {useSearchParams} from "react-router-dom";

export const Tiers = ({tiers, remotePost, formMethods}) => {
    const theme = useTheme();
    const mediaContext = useMediaContext();

    const [daysActive, setDaysActive] = useState(90);
    const [paymentType, setPaymentType] = useState(""); // Betalingskort or Mobilepay
    const didMountRef = useRef(false); // Track first render

    const isRemote = remotePost != null;

    const isMobileSize = mediaContext.isMobileSize;

    // When a post is first created or expired the prices are fixed
    const getDefaultTiers = (tiers) => {
        const shownTiers = tiers.reduce((acc, tier) => {
            const priceNow = Math.max(0, oreToKroner(tier.pricePerDay * daysActive));
            const priceBefore = Math.max(0, oreToKroner(tier.originalPricePerDay * daysActive));
            const discount = 100 - Math.ceil((priceNow / priceBefore) * 100);

            acc.push({
                disabled: false,  // No tiers are disabled.
                paid: false,      // No tiers are marked as paid.
                price: priceNow,
                originalPrice: priceBefore,
                ...tier,
                // Overwrite certain properties if necessary
                discount: discount,
            });
            return acc;
        }, []);

        return shownTiers.reverse(); // Show more premium tiers first
    }

    // When a post is created and not expired the prices are dynamic
    const getLiveTiers = (tiers, post) => {
        // Subtract the cost of any previously purchased tier
        let dailyDeductible = 0;
        let originalDailyDeductible = 0;

        const shownTiers = tiers.reduce((acc, tier) => {
            let features = tier.features;
            let disabled = false;
            let paid = false;

            if (post.Tier != null) {
                disabled = tier.priority > post.Tier.Priority;
                paid = tier.id === post.Tier.Id;

                // Copy the actual features of the post, in case they have changed since purchase
                if (tier.id == post.Tier.Id) {
                    features = post.Tier.Features.map(feature => ({
                        id: feature.Id,
                        name: feature.Name,
                        description: feature.Description,
                        priority: feature.Priority
                    }));

                    // Because remotePost still use PascalCase, we cannot do this:
                    // features = post.Tier.Features;
                }

                dailyDeductible = post.Tier.PricePerDay;
                originalDailyDeductible = post.Tier.OriginalPricePerDay;
            }

            const priceNow = Math.max(0, oreToKroner(( tier.pricePerDay - dailyDeductible) * post.DaysLeft));
            const priceBefore = Math.max(0, oreToKroner((tier.originalPricePerDay - originalDailyDeductible) * post.DaysLeft));
            const discount = 100 - Math.ceil((priceNow / priceBefore) * 100);

            acc.push({
                disabled: disabled,
                paid: paid,
                price: priceNow,
                ...tier,  // Copy all the tier properties
                // Overwrite certain properties if necessary
                features: features,
                discount: disabled || paid ? 0 : discount,
                originalPrice: disabled || paid ? 0 : priceBefore,
            });
            return acc;
        }, []);

        return shownTiers.reverse(); // Show more premium tiers first
    }

    // When a post is first created, or when it is expired, the default tiers are shown. However, if the post
    // is live, the tiers are adjusted based on the post's current tier and the remaining time it has left.
    const initialShownTiers = (isRemote && !remotePost.Expired) ? getLiveTiers(tiers, remotePost) : getDefaultTiers(tiers);
    const [shownTiers, setShownTiers] = useState(initialShownTiers);
    const [selectedTier, _setSelectedTier] = useState(initialShownTiers[0]);  // default to most premium tier
    const lastTier = shownTiers?.at(-1);

    const [searchParams, setSearchParams] = useSearchParams();
    const tierParamId = searchParams.get("tier");

    const lessThanOneDayBeforeExpiration = isRemote && !remotePost.Expired && remotePost.SecondsLeft < 86400; // If there is less than one day before the post expires
    const atLeastOneDayBeforeExpiration = !isRemote || remotePost.Expired || remotePost.SecondsLeft >= 86400; // If the post has at least one day left before expiring

    const setSelectedTier = (id, tiers = shownTiers) => {
        const tier = tiers?.find(tier => tier.id == id);

        if (id == null) {
            _setSelectedTier(null);
        } else {
            _setSelectedTier({
                id: id,
                name: tier?.name,
                price: tier?.price,
                paid: tier?.paid,
            });
        }
    }

    useEffect(() => {
        if (isRemote && !remotePost.Expired && remotePost.Tier != null) {
            setSelectedTier(remotePost.Tier.Id, initialShownTiers);
        }

        // Retain selected tier if returning from payment
        if (tierParamId != null) {
            setSelectedTier(tierParamId, initialShownTiers);
        }
    }, []);

    useEffect(() => {
        const id = selectedTier?.id;
        formMethods.setValue("tier", id, {shouldValidate: id != null});
    }, [selectedTier])

    useEffect(() => {
        formMethods.setValue("payment", paymentType, {shouldValidate: true})
    }, [paymentType])

    useEffect(() => {
        // Skip running on initial mount
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        setShownTiers(getDefaultTiers(tiers))
        formMethods.setValue("days", daysActive, {shouldValidate: false});
    }, [daysActive])

    useEffect(() => {
        // Register payment field manually
        formMethods.register(
            "payment",
            {
                validate: async (payment) => {
                    // Require payment method for unpaid paid tiers
                    if (selectedTier == null || (selectedTier.price > 0 && payment == "" && selectedTier.paid == false)) {
                        return "Du skal vælge en betalingsmetode";
                    }
                }
            }
        );
    }, [selectedTier, shownTiers]);

    return <>
        <Section title="Sælg varen">
            {/* If the post is new or expired and the user is business */}
            {((!isRemote || remotePost.Expired) && accountStore.user.isBusinessUser) && (
                <Box style={isMobileSize ? {padding: "20px 0 20px 20px"} : {padding: "20px 0 0 27px"}}>
                    <FormControl >
                        <InputLabel id="days-active-label">Dage</InputLabel>
                        <Select
                            sx={{
                                minWidth: "100px",
                                backgroundColor: "white"
                            }}
                            label={"Dage"}
                            labelId="days-active-label"
                            value={daysActive}
                            onChange={(event) => setDaysActive(event.target.value)}
                        >
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={90}>90</MenuItem>
                            <MenuItem value={180}>180</MenuItem>
                            <MenuItem value={365}>365</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {lessThanOneDayBeforeExpiration && (
                <Typography sx={{
                    padding: isMobileSize ? "15px 20px" : "20px 20px 20px 20px",
                    color: theme.palette.grey[700]
                }}>
                    Du kan ikke ændre pakke, da annoncen udløber om under et døgn.
                </Typography>
            )}

            {atLeastOneDayBeforeExpiration && (
                <Fragment>
                    {formMethods.formState.errors["tier"] && (
                        <ErrorText>
                            {formMethods.formState.errors["tier"].message}
                        </ErrorText>
                    )}

                    {/* Mobile sælg varen */}
                    {isMobileSize && (
                        <Box>
                            {shownTiers.map((tier) => (
                                <TierRow
                                    key={tier.Name}
                                    tier={tier}
                                    checked={selectedTier?.id === tier.id}
                                    onClick={() => setSelectedTier(tier.id)}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Web sælg varen */}
                    {!isMobileSize && (
                        <div style={{
                            display: "flex",
                            padding: "20px",
                            alignItems: "end",
                            width: "fit-content"
                        }}>
                            <Column>
                                <Row sx={{alignItems: "end"}}>
                                    {shownTiers.map((tier, index) => {

                                        // Do not show the last tier here
                                        if (index == shownTiers.length - 1) {
                                            return
                                        }

                                        return <Fragment key={tier.name}>
                                            <Space width={"7px"}/>
                                            <TierCard
                                                tier={tier}
                                                selected={selectedTier?.id == tier.id}
                                                onClick={() => setSelectedTier(tier.id)}
                                            />
                                            <Space width={"7px"}/>
                                        </Fragment>
                                    })}
                                </Row>

                                <Space height={"5px"}/>

                                 {/*The last tier is shown differently*/}
                                <LastTierCard
                                    tier={lastTier}
                                    selected={selectedTier?.id === lastTier.id}
                                    onClick={() => setSelectedTier(lastTier.id)}
                                />

                            </Column>
                        </div>
                    )}

                    <Typography sx={{
                        padding: isMobileSize ? "10px 20px" : "0px 20px 20px 20px",
                        color: theme.palette.grey[700]
                    }}>
                        {(isRemote && !remotePost.Expired)
                            ? `Bemærk, annoncen udløber om ${remotePost.DaysLeft < 10 ? "under " : ""} ${remotePost.DaysLeft > 1 ? `${remotePost.DaysLeft} dage.` : `${Math.ceil((remotePost.EndDate - getUnixTime()) / (60 * 60))} timer.`}`
                            : `Bemærk, din annonce er aktiv i ${daysActive} dage.`
                        }

                    </Typography>
                </Fragment>
            )}
        </Section>

        {atLeastOneDayBeforeExpiration && selectedTier != null && selectedTier.price > 0 && selectedTier.paid == false && (
            <Section title="Vælg betalingsform">
                <Box sx={{padding: "10px 10px"}}>
                    <PaymentItem
                        onClick={() => setPaymentType('CreditCard')}
                        error={formMethods.formState.errors["payment"]}
                    >
                        <CenterRow>
                            <Radio checked={paymentType === 'CreditCard'}/>
                            <Typography>Betalingskort</Typography>
                        </CenterRow>
                        {mediaContext.isUnderSm
                            ? <PictureRender image="payment-credit-card" size={117}/>
                            : <PictureRender image="payment-credit-card" size={206}/>
                        }
                    </PaymentItem>

                    <PaymentItem
                        onClick={() => setPaymentType('MobilePay')}
                        error={formMethods.formState.errors["payment"]}
                    >
                        <CenterRow>
                            <Radio checked={paymentType === 'MobilePay'}/>
                            <Typography>MobilePay</Typography>
                        </CenterRow>
                        {mediaContext.isUnderSm
                            ? <PictureRender image="payment-mobile-pay-filled" size={40}/>
                            : <PictureRender image="payment-mobile-pay-filled" size={56}/>
                        }
                    </PaymentItem>

                    {formMethods.formState.errors["payment"] && (
                        <ErrorText sx={{fontSize: "13px", p: "10px 0 0 10px"}}>
                            {formMethods.formState.errors["payment"].message}
                        </ErrorText>
                    )}

                    <Space height="10px"/>
                </Box>
            </Section>
        )}
    </>
}

